class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.checkoutSubmitting = false;
    this.checkoutResetTimer = null;
    this.handleDocumentCheckoutClick = this.handleDocumentCheckoutClick.bind(this);
    this.handlePageShow = this.handlePageShow.bind(this);
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlay();
    document.addEventListener('click', this.handleDocumentCheckoutClick);
    window.addEventListener('pageshow', this.handlePageShow);
    this.setHeaderCartIconAccessibility();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentCheckoutClick);
    window.removeEventListener('pageshow', this.handlePageShow);
    window.clearTimeout(this.checkoutResetTimer);
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay || overlay.dataset.kaivoBound === 'true') return;

    overlay.dataset.kaivoBound = 'true';
    overlay.addEventListener('click', this.close.bind(this));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    const drawerInner = this.querySelector('.drawer__inner');
    let focusWasSet = false;

    const setDrawerFocus = () => {
      if (focusWasSet || !this.classList.contains('active')) return;
      focusWasSet = true;

      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = drawerInner || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    };

    const handleTransitionEnd = (event) => {
      if (event.target !== drawerInner || event.propertyName !== 'transform') return;
      drawerInner.removeEventListener('transitionend', handleTransitionEnd);
      setDrawerFocus();
    };

    if (drawerInner) drawerInner.addEventListener('transitionend', handleTransitionEnd);
    window.setTimeout(setDrawerFocus, 620);

    this.classList.add('animate');
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => this.classList.add('active'));
    });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    if (this.checkoutSubmitting) return;
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  handleDocumentCheckoutClick(event) {
    if (event.defaultPrevented) return;

    const checkoutButton = event.target.closest('[name="checkout"]');
    const transition = this.querySelector('[data-kaivo-checkout-transition]');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isDrawerCheckout = this.contains(checkoutButton);

    if (!checkoutButton || !checkoutButton.form || checkoutButton.disabled) return;

    if (this.checkoutSubmitting) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    this.checkoutSubmitting = true;
    checkoutButton.setAttribute('aria-busy', 'true');

    if (isDrawerCheckout && !reduceMotion && transition) {
      transition.hidden = false;
      document.body.classList.add('kaivo-checkout-loading');
      window.requestAnimationFrame(() => {
        this.classList.add('is-checkout-transitioning');
      });
    }

    this.checkoutResetTimer = window.setTimeout(() => this.resetCheckoutTransition(), 8000);
    this.syncCartAndCheckout(checkoutButton.form);
  }

  syncCartAndCheckout(form) {
    if (!form.reportValidity()) {
      this.resetCheckoutTransition();
      return;
    }

    const updates = Array.from(form.querySelectorAll('[name="updates[]"]'), (input) => Number(input.value));
    const checkoutUrl = `${window.Shopify.routes.root}checkout`;

    if (!updates.length) {
      window.location.assign(checkoutUrl);
      return;
    }

    fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates }),
    })
      .then((response) => {
        if (!response.ok) throw new Error('Cart synchronization failed');
        return response.json();
      })
      .then(() => window.location.assign(checkoutUrl))
      .catch(() => window.location.assign(checkoutUrl));
  }

  handlePageShow() {
    this.resetCheckoutTransition();
  }

  resetCheckoutTransition() {
    const transition = this.querySelector('[data-kaivo-checkout-transition]');
    const checkoutButtons = this.querySelectorAll('[name="checkout"][aria-busy="true"]');

    window.clearTimeout(this.checkoutResetTimer);
    this.checkoutSubmitting = false;
    this.classList.remove('is-checkout-transitioning');
    document.body.classList.remove('kaivo-checkout-loading');

    checkoutButtons.forEach((button) => {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    });

    if (transition) transition.hidden = true;
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.bindOverlay();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
